/**
 * The motif library behind the data-driven invitation templates
 * (lib/inviteTemplates.ts).
 *
 * Every motif is one hand-drawn icon in a 100×100 box, coloured only through
 * the invitation's own custom properties — `--inv-a1`, `--inv-a2`,
 * `--inv-gold`, `--inv-ink`, `--inv-paper` — so a single drawing serves any
 * palette a template (or a host override) puts under it. Icons return SVG
 * children, not an <svg>: TemplateMotifLayer wraps them at viewport scale and
 * TemplateThumb at thumbnail scale.
 *
 * A motif also names the rest of a template's decoration kit: the shape that
 * falls down the page (`fall`, a 20×20 path used by InvitationPage's
 * DecorLayer), the characters that twinkle, the footer glyphs and the marker
 * before a schedule row's venue.
 */

import type { ReactElement } from 'react'

// Palette handles. Opacity is done per shape, never by picking a lighter
// colour, so the drawing stays right on dark paper.
const A = 'var(--inv-a1)'
const B = 'var(--inv-a2)'
const G = 'var(--inv-gold)'
const I = 'var(--inv-ink)'
const P = 'var(--inv-paper)'

/* ------------------------------------------------------ falling particles */

export const PARTICLES: Record<string, string> = {
  heart: 'M10 18 C 4.5 13, 1.5 9.4, 1.5 6.4 C 1.5 3.4, 3.9 1.8, 6.2 1.8 C 7.9 1.8, 9.3 2.7, 10 4.1 C 10.7 2.7, 12.1 1.8, 13.8 1.8 C 16.1 1.8, 18.5 3.4, 18.5 6.4 C 18.5 9.4, 15.5 13, 10 18 Z',
  star: 'M10 1 L12.6 7.2 L19.4 7.7 L14.2 12.1 L15.8 18.8 L10 15.2 L4.2 18.8 L5.8 12.1 L0.6 7.7 L7.4 7.2 Z',
  petal: 'M10 1 C 16 5, 16 13, 10 19 C 4 13, 4 5, 10 1z',
  spark: 'M10 0 L12.4 7.6 L20 10 L12.4 12.4 L10 20 L7.6 12.4 L0 10 L7.6 7.6 Z',
  dot: 'M10 3 a7 7 0 1 0 0.01 0z',
  leaf: 'M3 17 C 3 7, 9 3, 17 3 C 17 11, 11 17, 3 17z',
  snow: 'M10 0 L12 7 L19 5 L14 10 L19 15 L12 13 L10 20 L8 13 L1 15 L6 10 L1 5 L8 7 Z',
  bolt: 'M11 1 L4 11 h5 l-1 8 L16 8 h-5z',
  balloon: 'M10 1 C 5 1, 3 5, 3 8.5 C 3 12.5, 7 15, 10 16 C 13 15, 17 12.5, 17 8.5 C 17 5, 15 1, 10 1z M9 16 l1 1.5 1 -1.5 l-.5 2.5h-1z',
  diamond: 'M10 1 L19 8 L10 19 L1 8z',
  note: 'M8 3 v10.5 a3 3 0 1 0 2 2.8 V6 l6 -1.5 V2z',
  drop: 'M10 1 C 10 1, 3 9, 3 13 a7 7 0 0 0 14 0 C 17 9, 10 1, 10 1z',
  egg: 'M10 1 C 5 1, 3 8, 3 12 a7 7 0 0 0 14 0 C 17 8, 15 1, 10 1z',
  pennant: 'M2 2 h16 l-6 8 6 8 H2z',
  confetti: 'M4 6 l10 -4 l2 6 l-10 4z',
  moon: 'M13 2 a8 8 0 1 0 5 14 a6.5 6.5 0 1 1 -5 -14z',
  square: 'M4 4 h12 v12 H4z',
}

/* --------------------------------------------------------------- helpers */

const Star = ({ x, y, s = 1, fill = G, o = 1 }: { x: number; y: number; s?: number; fill?: string; o?: number }) => (
  <path
    d="M0 -10 L2.6 -3.4 L9.5 -2.9 L4.2 1.5 L5.9 8.3 L0 4.6 L-5.9 8.3 L-4.2 1.5 L-9.5 -2.9 L-2.6 -3.4 Z"
    transform={`translate(${x} ${y}) scale(${s})`}
    fill={fill}
    opacity={o}
  />
)

const Spark = ({ x, y, s = 1, fill = G, o = 1 }: { x: number; y: number; s?: number; fill?: string; o?: number }) => (
  <path
    d="M0 -10 L2.4 -2.4 L10 0 L2.4 2.4 L0 10 L-2.4 2.4 L-10 0 L-2.4 -2.4 Z"
    transform={`translate(${x} ${y}) scale(${s})`}
    fill={fill}
    opacity={o}
  />
)

const Dot = ({ x, y, r = 3, fill = G, o = 1 }: { x: number; y: number; r?: number; fill?: string; o?: number }) => (
  <circle cx={x} cy={y} r={r} fill={fill} opacity={o} />
)

/* ------------------------------------------------------------------ icons */

export type Motif = {
  Icon: () => ReactElement | null
  /** Key in PARTICLES. */
  fall: string
  chars: string[]
  foot: string
  where: string
}

const ICONS: Record<string, () => ReactElement | null> = {
  none: () => null,

  dino: () => (
    <>
      <path d="M18 78 C 10 70, 12 58, 22 50 C 30 44, 40 46, 46 40 C 50 36, 50 28, 58 26 C 70 24, 78 32, 76 42 L 70 46 L 76 50 L 68 52 C 66 60, 60 64, 52 66 L 50 78 L 42 78 L 44 68 L 36 70 L 34 78 L 26 78 L 28 68 C 24 72, 20 76, 18 78 Z" fill={A} />
      <path d="M26 50 l4 -8 4 8 M36 45 l4 -8 4 8 M46 40 l3 -7 4 7" fill={B} />
      <circle cx="64" cy="34" r="2.6" fill={P} />
      <circle cx="64.6" cy="34.4" r="1.2" fill={I} />
      <path d="M8 84 C 10 80, 13 78, 18 78 L 14 84 Z" fill={A} />
      <path d="M6 88 h88" stroke={B} strokeWidth="2" strokeLinecap="round" opacity=".5" />
      <Dot x={86} y={70} r={3} fill={B} o={0.8} />
    </>
  ),

  car: () => (
    <>
      <path d="M10 62 h8 l8 -14 h30 l10 14 h20 a6 6 0 0 1 6 6 v6 H10 Z" fill={A} />
      <path d="M30 50 h22 l6 10 H26 Z" fill={P} opacity=".85" />
      <rect x="10" y="66" width="82" height="6" fill={B} opacity=".55" />
      <circle cx="28" cy="74" r="9" fill={I} />
      <circle cx="28" cy="74" r="4" fill={P} />
      <circle cx="74" cy="74" r="9" fill={I} />
      <circle cx="74" cy="74" r="4" fill={P} />
      <path d="M12 40 h9 v-9 h-9z M21 31 h9 v9 h-9z M12 31 h9 v-9 h-9z M21 22 h9 v9 h-9z" fill={I} opacity=".8" />
      <path d="M12 22 v34" stroke={I} strokeWidth="2" />
      <path d="M86 46 l10 -4 M84 52 l12 -2" stroke={B} strokeWidth="3" strokeLinecap="round" />
    </>
  ),

  truck: () => (
    <>
      <path d="M14 46 h26 v-14 h24 l14 14 h8 v14 H14 Z" fill={A} />
      <path d="M44 36 h16 l8 10 H44 Z" fill={P} opacity=".85" />
      <circle cx="30" cy="70" r="15" fill={I} />
      <circle cx="30" cy="70" r="6" fill={B} />
      <circle cx="72" cy="70" r="15" fill={I} />
      <circle cx="72" cy="70" r="6" fill={B} />
      <path d="M8 90 q10 -6 20 0 q10 6 20 0 q10 -6 20 0 q10 6 20 0" fill="none" stroke={B} strokeWidth="3" strokeLinecap="round" opacity=".6" />
      <path d="M52 22 l6 -10 6 10z" fill={B} />
    </>
  ),

  rocket: () => (
    <>
      <path d="M50 8 C 62 20, 66 40, 60 62 H 40 C 34 40, 38 20, 50 8 Z" fill={A} />
      <circle cx="50" cy="36" r="7" fill={P} />
      <circle cx="50" cy="36" r="4" fill={B} opacity=".8" />
      <path d="M40 50 L 26 66 L 40 62 Z M60 50 L 74 66 L 60 62 Z" fill={B} />
      <path d="M44 62 h12 l-3 8 h-6 z" fill={I} opacity=".6" />
      <path d="M45 70 C 46 82, 54 82, 55 70 C 53 76, 47 76, 45 70 Z" fill={G} />
      <circle cx="20" cy="26" r="7" fill={B} opacity=".8" />
      <ellipse cx="20" cy="26" rx="12" ry="3" fill="none" stroke={G} strokeWidth="1.6" transform="rotate(-20 20 26)" />
      <Star x={80} y={20} s={0.6} />
      <Star x={84} y={50} s={0.4} />
      <Dot x={14} y={60} r={1.6} />
    </>
  ),

  unicorn: () => (
    <>
      <path d="M30 78 C 22 60, 30 40, 48 34 L 60 30 L 62 38 C 70 40, 74 48, 70 58 C 66 66, 56 70, 50 68 L 46 80 Z" fill={A} opacity=".9" />
      <path d="M52 32 L 58 6 L 62 33 Z" fill={G} />
      <path d="M40 40 C 30 36, 26 44, 32 50 C 24 50, 24 60, 32 62 C 26 66, 28 74, 36 74" fill="none" stroke={B} strokeWidth="6" strokeLinecap="round" />
      <circle cx="60" cy="46" r="2.6" fill={I} />
      <path d="M66 58 q4 2 6 0" stroke={I} strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <Star x={82} y={22} s={0.6} fill={B} />
      <Star x={20} y={22} s={0.45} />
      <Dot x={86} y={72} r={2} fill={B} />
    </>
  ),

  crown: () => (
    <>
      <path d="M18 70 L 12 30 L 32 48 L 50 20 L 68 48 L 88 30 L 82 70 Z" fill={A} />
      <rect x="18" y="70" width="64" height="10" rx="3" fill={B} />
      <circle cx="12" cy="28" r="4" fill={G} />
      <circle cx="50" cy="18" r="4.5" fill={G} />
      <circle cx="88" cy="28" r="4" fill={G} />
      <circle cx="50" cy="58" r="5" fill={G} />
      <circle cx="32" cy="62" r="3" fill={P} opacity=".7" />
      <circle cx="68" cy="62" r="3" fill={P} opacity=".7" />
    </>
  ),

  wand: () => (
    <>
      <path d="M22 84 L 62 44" stroke={B} strokeWidth="6" strokeLinecap="round" />
      <path d="M22 84 L 62 44" stroke={G} strokeWidth="2" strokeLinecap="round" opacity=".6" />
      <Star x={70} y={34} s={1.6} fill={G} />
      <Star x={86} y={18} s={0.5} fill={A} />
      <Star x={54} y={16} s={0.6} fill={A} />
      <Spark x={88} y={52} s={0.5} fill={B} />
      <Dot x={40} y={26} r={2} fill={A} />
      <Dot x={82} y={80} r={2.2} fill={B} />
    </>
  ),

  fish: () => (
    <>
      <path d="M20 50 C 34 30, 60 30, 74 50 C 60 70, 34 70, 20 50 Z" fill={A} />
      <path d="M74 50 L 92 36 L 88 50 L 92 64 Z" fill={A} />
      <path d="M36 50 C 44 42, 56 42, 64 50" stroke={P} strokeWidth="2" fill="none" opacity=".7" />
      <circle cx="30" cy="46" r="3" fill={P} />
      <circle cx="31" cy="46" r="1.4" fill={I} />
      <path d="M44 34 C 48 28, 56 28, 58 34" fill={B} />
      <path d="M12 86 C 22 78, 30 80, 36 86" stroke={B} strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <path d="M78 84 a6 6 0 1 1 8 -6 l-4 8 z" fill={G} opacity=".9" />
      <circle cx="16" cy="26" r="3" fill="none" stroke={B} strokeWidth="1.6" />
      <circle cx="24" cy="14" r="2" fill="none" stroke={B} strokeWidth="1.4" />
      <circle cx="10" cy="14" r="1.6" fill="none" stroke={B} strokeWidth="1.2" />
    </>
  ),

  ship: () => (
    <>
      <path d="M14 62 h72 l-10 18 H24 Z" fill={I} />
      <path d="M48 16 v46" stroke={I} strokeWidth="3" />
      <path d="M50 20 C 70 26, 74 44, 70 56 H 50 Z" fill={A} />
      <path d="M46 26 C 30 32, 26 46, 30 56 H 46 Z" fill={P} stroke={A} strokeWidth="2" />
      <path d="M48 16 l14 4 -14 4z" fill={B} />
      <path d="M6 88 q10 -6 20 0 q10 6 20 0 q10 -6 20 0 q10 6 20 0 q6 -4 10 -2" fill="none" stroke={B} strokeWidth="3" strokeLinecap="round" opacity=".7" />
      <circle cx="86" cy="18" r="6" fill={G} />
      <circle cx="86" cy="18" r="3" fill="none" stroke={B} strokeWidth="1.2" />
    </>
  ),

  bolt: () => (
    <>
      <circle cx="50" cy="50" r="38" fill={B} opacity=".22" />
      <path d="M56 8 L 26 54 h20 l-6 38 L 74 44 H 54 Z" fill={A} />
      <path d="M56 8 L 26 54 h20 l-6 38" fill="none" stroke={G} strokeWidth="2" opacity=".7" />
      <Star x={86} y={20} s={0.5} fill={G} />
      <Star x={14} y={80} s={0.5} fill={G} />
    </>
  ),

  giraffe: () => (
    <>
      <path d="M40 92 C 42 70, 44 50, 52 30 L 58 24 L 66 28 L 62 36 C 56 50, 54 70, 56 92 Z" fill={A} />
      <path d="M50 30 C 56 20, 68 20, 72 28 C 74 34, 70 40, 62 40 C 56 40, 52 36, 50 30 Z" fill={A} />
      <path d="M54 14 l4 8 M66 12 l-2 10" stroke={A} strokeWidth="3" strokeLinecap="round" />
      <circle cx="54" cy="12" r="2.8" fill={B} />
      <circle cx="66" cy="10" r="2.8" fill={B} />
      <circle cx="46" cy="52" r="3" fill={B} />
      <circle cx="54" cy="66" r="3.4" fill={B} />
      <circle cx="46" cy="80" r="2.6" fill={B} />
      <circle cx="66" cy="30" r="2.2" fill={I} />
      <path d="M12 86 C 18 78, 26 78, 32 86 M14 70 C 18 62, 26 62, 30 70" fill={B} opacity=".8" />
      <path d="M22 62 v30" stroke={B} strokeWidth="2" />
      <path d="M80 60 c 8 -8, 14 -4, 12 6 c -6 2, -12 -2, -12 -6z M82 74 c 8 -8, 14 -4, 12 6 c -6 2, -12 -2, -12 -6z" fill={B} />
    </>
  ),

  monstera: () => (
    <>
      <path d="M50 92 C 20 84, 8 56, 22 30 C 34 10, 66 10, 78 30 C 92 56, 80 84, 50 92 Z" fill={A} />
      <path d="M50 92 V 34" stroke={P} strokeWidth="2.5" opacity=".6" />
      <path d="M30 40 L 50 56 M70 40 L 50 56 M28 62 L 50 72 M72 62 L 50 72" stroke={P} strokeWidth="2" opacity=".5" />
      <path d="M22 30 C 30 36, 36 48, 34 60 C 26 56, 20 44, 22 30 Z M78 30 C 70 36, 64 48, 66 60 C 74 56, 80 44, 78 30 Z" fill={P} />
      <path d="M32 74 C 38 74, 44 80, 44 88 C 38 86, 34 80, 32 74 Z M68 74 C 62 74, 56 80, 56 88 C 62 86, 66 80, 68 74 Z" fill={P} />
      <Dot x={86} y={82} r={4} fill={B} />
      <Dot x={14} y={86} r={2.5} fill={B} o={0.8} />
    </>
  ),

  barn: () => (
    <>
      <path d="M18 88 V 48 L 50 22 L 82 48 V 88 Z" fill={A} />
      <path d="M12 50 L 50 18 L 88 50" fill="none" stroke={I} strokeWidth="4" strokeLinecap="round" />
      <path d="M40 88 V 62 h20 v26 z" fill={I} opacity=".85" />
      <path d="M40 62 l20 26 M60 62 l-20 26" stroke={P} strokeWidth="2" opacity=".6" />
      <rect x="44" y="36" width="12" height="12" rx="2" fill={P} />
      <circle cx="84" cy="18" r="8" fill={G} />
      <path d="M6 92 h88" stroke={B} strokeWidth="3" strokeLinecap="round" opacity=".7" />
    </>
  ),

  excavator: () => (
    <>
      <rect x="14" y="52" width="40" height="22" rx="4" fill={A} />
      <rect x="20" y="40" width="20" height="16" rx="3" fill={A} />
      <rect x="24" y="44" width="12" height="9" fill={P} opacity=".85" />
      <path d="M54 58 L 76 34 L 82 40 L 66 60 Z" fill={A} />
      <path d="M76 34 L 90 46 L 84 56 L 80 42 Z" fill={I} />
      <rect x="10" y="74" width="50" height="12" rx="6" fill={I} />
      <circle cx="20" cy="80" r="4" fill={P} opacity=".6" />
      <circle cx="35" cy="80" r="4" fill={P} opacity=".6" />
      <circle cx="50" cy="80" r="4" fill={P} opacity=".6" />
      <path d="M74 88 l6 -18 l6 18 z" fill={A} />
      <path d="M77 80 h6" stroke={P} strokeWidth="2" />
      <path d="M4 22 h30 v8 H4z" fill={G} />
      <path d="M8 22 l-4 8 M16 22 l-4 8 M24 22 l-4 8 M32 22 l-4 8" stroke={I} strokeWidth="2.5" />
    </>
  ),

  train: () => (
    <>
      <rect x="10" y="44" width="34" height="28" rx="4" fill={A} />
      <rect x="44" y="56" width="42" height="16" rx="3" fill={B} />
      <rect x="16" y="30" width="18" height="16" rx="3" fill={A} />
      <rect x="20" y="34" width="10" height="8" fill={P} opacity=".85" />
      <rect x="60" y="42" width="10" height="14" rx="2" fill={B} />
      <circle cx="22" cy="78" r="7" fill={I} />
      <circle cx="40" cy="78" r="7" fill={I} />
      <circle cx="58" cy="78" r="6" fill={I} />
      <circle cx="78" cy="78" r="6" fill={I} />
      <path d="M86 72 h8 v-8 h-8" fill={A} />
      <circle cx="70" cy="26" r="6" fill={P} stroke={B} strokeWidth="1.5" opacity=".8" />
      <circle cx="80" cy="16" r="4.5" fill={P} stroke={B} strokeWidth="1.5" opacity=".7" />
      <circle cx="88" cy="10" r="3" fill={P} stroke={B} strokeWidth="1.2" opacity=".6" />
      <path d="M4 90 h92" stroke={I} strokeWidth="2.5" opacity=".7" />
    </>
  ),

  'cloud-rainbow': () => (
    <>
      <path d="M14 70 A 36 36 0 0 1 86 70" fill="none" stroke={A} strokeWidth="7" strokeLinecap="round" />
      <path d="M22 70 A 28 28 0 0 1 78 70" fill="none" stroke={G} strokeWidth="7" strokeLinecap="round" />
      <path d="M30 70 A 20 20 0 0 1 70 70" fill="none" stroke={B} strokeWidth="7" strokeLinecap="round" />
      <path d="M20 84 a10 10 0 0 1 8 -14 a12 12 0 0 1 22 2 a9 9 0 0 1 4 12 z" fill={P} stroke={A} strokeWidth="2" />
      <path d="M60 84 a10 10 0 0 1 8 -12 a12 12 0 0 1 20 4 a8 8 0 0 1 2 8 z" fill={P} stroke={A} strokeWidth="2" />
    </>
  ),

  teddy: () => (
    <>
      <circle cx="26" cy="30" r="12" fill={A} />
      <circle cx="74" cy="30" r="12" fill={A} />
      <circle cx="26" cy="30" r="6" fill={B} opacity=".6" />
      <circle cx="74" cy="30" r="6" fill={B} opacity=".6" />
      <circle cx="50" cy="52" r="32" fill={A} />
      <ellipse cx="50" cy="62" rx="14" ry="11" fill={P} opacity=".85" />
      <ellipse cx="50" cy="58" rx="5" ry="3.5" fill={I} />
      <path d="M50 62 v5 M45 70 q5 4 10 0" stroke={I} strokeWidth="2" fill="none" strokeLinecap="round" />
      <circle cx="38" cy="46" r="3" fill={I} />
      <circle cx="62" cy="46" r="3" fill={I} />
      <path d="M40 88 l10 -8 10 8" fill={B} />
    </>
  ),

  ballet: () => (
    <>
      <path d="M22 70 C 22 50, 34 30, 44 30 C 52 30, 54 44, 50 58 C 48 68, 40 76, 30 78 C 26 78, 22 76, 22 70 Z" fill={A} />
      <path d="M46 78 C 46 58, 58 38, 68 38 C 76 38, 78 52, 74 66 C 72 76, 64 84, 54 86 C 50 86, 46 84, 46 78 Z" fill={A} />
      <path d="M32 44 C 40 40, 46 42, 48 50 M56 52 C 64 48, 70 50, 72 58" stroke={P} strokeWidth="2" fill="none" opacity=".8" />
      <path d="M44 30 C 30 20, 22 24, 16 14 M44 30 C 52 18, 60 22, 66 12" stroke={B} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M68 38 C 60 26, 70 20, 80 16" stroke={B} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <Dot x={86} y={70} r={3} fill={G} />
      <Dot x={12} y={82} r={2} fill={G} />
    </>
  ),

  controller: () => (
    <>
      <path d="M22 34 h56 a18 18 0 0 1 16 24 l-6 18 a8 8 0 0 1 -14 2 l-6 -10 H 32 l-6 10 a8 8 0 0 1 -14 -2 l-6 -18 a18 18 0 0 1 16 -24 Z" fill={A} />
      <path d="M30 48 h12 M36 42 v12" stroke={P} strokeWidth="4" strokeLinecap="round" />
      <circle cx="66" cy="44" r="3.5" fill={B} />
      <circle cx="74" cy="50" r="3.5" fill={G} />
      <circle cx="58" cy="50" r="3.5" fill={G} />
      <circle cx="66" cy="56" r="3.5" fill={B} />
      <path d="M8 16 h6 v6 h-6z M20 12 h6 v6 h-6z M86 14 h6 v6 h-6z" fill={B} opacity=".8" />
    </>
  ),

  butterfly: () => (
    <>
      <path d="M50 50 C 36 20, 10 18, 10 40 C 10 54, 28 58, 48 54 Z" fill={A} />
      <path d="M50 50 C 64 20, 90 18, 90 40 C 90 54, 72 58, 52 54 Z" fill={A} />
      <path d="M50 54 C 38 52, 18 60, 20 74 C 22 84, 40 78, 50 62 Z" fill={B} />
      <path d="M50 54 C 62 52, 82 60, 80 74 C 78 84, 60 78, 50 62 Z" fill={B} />
      <ellipse cx="50" cy="52" rx="3.5" ry="14" fill={I} />
      <path d="M48 40 C 44 30, 40 26, 36 24 M52 40 C 56 30, 60 26, 64 24" stroke={I} strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <circle cx="26" cy="38" r="4" fill={G} opacity=".8" />
      <circle cx="74" cy="38" r="4" fill={G} opacity=".8" />
    </>
  ),

  circus: () => (
    <>
      <path d="M12 46 C 30 30, 70 30, 88 46 L 50 40 Z" fill={A} />
      <path d="M12 46 L 88 46 L 80 86 H 20 Z" fill={A} />
      <path d="M26 46 l6 40 M40 46 l3 40 M60 46 l-3 40 M74 46 l-6 40" stroke={P} strokeWidth="5" opacity=".85" />
      <path d="M50 40 V 14" stroke={I} strokeWidth="2.5" />
      <path d="M50 14 l14 5 -14 5z" fill={B} />
      <path d="M42 86 v-20 a8 8 0 0 1 16 0 v20 z" fill={I} opacity=".8" />
      <Star x={14} y={20} s={0.6} fill={B} />
      <Star x={86} y={22} s={0.6} fill={B} />
    </>
  ),

  candy: () => (
    <>
      <circle cx="40" cy="44" r="18" fill={A} />
      <path d="M22 44 C 26 36, 30 52, 34 44 C 38 36, 42 52, 46 44 C 50 36, 54 52, 58 44" fill="none" stroke={P} strokeWidth="3" strokeLinecap="round" />
      <path d="M22 40 L 6 30 L 10 44 L 6 58 L 22 48 Z" fill={A} />
      <path d="M58 40 L 74 30 L 70 44 L 74 58 L 58 48 Z" fill={A} />
      <circle cx="76" cy="26" r="14" fill={B} />
      <path d="M76 26 m-8 0 a8 8 0 1 1 16 0 a4 4 0 1 1 -8 0" fill="none" stroke={P} strokeWidth="2.5" />
      <path d="M76 40 L 68 88" stroke={I} strokeWidth="4" strokeLinecap="round" />
      <Dot x={20} y={80} r={3} fill={G} />
      <Dot x={50} y={76} r={2.5} fill={B} />
    </>
  ),

  icecream: () => (
    <>
      <path d="M34 50 L 50 92 L 66 50 Z" fill={G} />
      <path d="M38 56 l24 0 M40 64 l20 0 M43 72 l14 0 M46 80 l8 0" stroke={I} strokeWidth="1.4" opacity=".4" />
      <circle cx="50" cy="30" r="14" fill={A} />
      <circle cx="37" cy="40" r="11" fill={B} />
      <circle cx="63" cy="40" r="11" fill={P} stroke={B} strokeWidth="1.5" />
      <path d="M50 16 l3 -8" stroke={I} strokeWidth="2.5" strokeLinecap="round" />
      <Dot x={44} y={26} r={1.6} fill={P} />
      <Dot x={54} y={24} r={1.6} fill={P} />
      <Dot x={36} y={36} r={1.4} fill={P} />
      <Dot x={84} y={70} r={3} fill={A} />
    </>
  ),

  pizza: () => (
    <>
      <path d="M12 22 A 60 60 0 0 1 88 22 L 50 90 Z" fill={G} />
      <path d="M12 22 A 60 60 0 0 1 88 22" fill="none" stroke={B} strokeWidth="8" strokeLinecap="round" />
      <path d="M18 30 A 52 52 0 0 1 82 30 L 50 84 Z" fill={A} opacity=".85" />
      <circle cx="40" cy="40" r="6" fill={B} />
      <circle cx="60" cy="42" r="6" fill={B} />
      <circle cx="50" cy="60" r="6" fill={B} />
      <circle cx="38" cy="56" r="3" fill={P} opacity=".7" />
      <circle cx="62" cy="58" r="3" fill={P} opacity=".7" />
    </>
  ),

  football: () => (
    <>
      <circle cx="50" cy="50" r="36" fill={P} stroke={B} strokeWidth="3" />
      <path d="M50 30 L 66 42 L 60 62 H 40 L 34 42 Z" fill={B} />
      <path d="M50 30 L 50 16 M66 42 L 80 36 M60 62 L 70 76 M40 62 L 30 76 M34 42 L 20 36" stroke={B} strokeWidth="3" strokeLinecap="round" />
      <path d="M50 16 A 34 34 0 0 1 80 36 M70 76 A 34 34 0 0 1 30 76 M20 36 A 34 34 0 0 1 50 16" fill="none" stroke={B} strokeWidth="2" opacity=".5" />
      <circle cx="12" cy="88" r="4" fill={A} />
      <circle cx="88" cy="12" r="4" fill={A} />
    </>
  ),

  palette: () => (
    <>
      <path d="M50 12 C 26 12, 10 30, 12 52 C 14 72, 30 86, 50 86 C 60 86, 60 76, 56 70 C 52 64, 58 58, 66 58 C 80 58, 88 50, 88 40 C 88 24, 72 12, 50 12 Z" fill={P} stroke={I} strokeWidth="2.5" />
      <circle cx="30" cy="42" r="6" fill={A} />
      <circle cx="42" cy="28" r="6" fill={B} />
      <circle cx="62" cy="26" r="6" fill={G} />
      <circle cx="30" cy="62" r="6" fill={I} opacity=".7" />
      <path d="M70 92 L 92 46" stroke={I} strokeWidth="5" strokeLinecap="round" />
      <path d="M90 42 l6 -8 -2 12 z" fill={A} />
    </>
  ),

  flask: () => (
    <>
      <path d="M40 12 h20 v26 l22 40 a6 6 0 0 1 -5 9 H 23 a6 6 0 0 1 -5 -9 l22 -40 Z" fill={P} stroke={I} strokeWidth="2.5" />
      <path d="M28 66 h44 l10 16 a2 2 0 0 1 -2 3 H 20 a2 2 0 0 1 -2 -3 Z" fill={A} />
      <circle cx="40" cy="74" r="3" fill={P} opacity=".8" />
      <circle cx="56" cy="78" r="2.2" fill={P} opacity=".8" />
      <circle cx="48" cy="60" r="2.5" fill={A} />
      <circle cx="54" cy="52" r="1.8" fill={A} />
      <ellipse cx="82" cy="24" rx="12" ry="4.5" fill="none" stroke={B} strokeWidth="1.6" transform="rotate(30 82 24)" />
      <ellipse cx="82" cy="24" rx="12" ry="4.5" fill="none" stroke={B} strokeWidth="1.6" transform="rotate(-30 82 24)" />
      <circle cx="82" cy="24" r="3" fill={B} />
    </>
  ),

  'moon-stars': () => (
    <>
      <path d="M62 10 A 38 38 0 1 0 90 62 A 30 30 0 1 1 62 10 Z" fill={A} />
      <Star x={22} y={22} s={0.8} fill={G} />
      <Star x={16} y={70} s={0.5} fill={G} />
      <Star x={80} y={84} s={0.6} fill={B} />
      <Dot x={40} y={12} r={1.8} fill={B} />
      <Dot x={8} y={44} r={1.4} fill={G} />
    </>
  ),

  sparkle: () => (
    <>
      <Spark x={50} y={44} s={2.6} fill={A} />
      <Spark x={22} y={26} s={0.9} fill={B} />
      <Spark x={80} y={22} s={0.7} fill={B} />
      <Spark x={78} y={72} s={1.1} fill={B} />
      <Spark x={20} y={78} s={0.6} fill={G} />
      <Dot x={90} y={48} r={1.6} fill={G} />
      <Dot x={12} y={52} r={2} fill={G} />
    </>
  ),

  disco: () => (
    <>
      <path d="M50 4 v10" stroke={I} strokeWidth="2.5" />
      <circle cx="50" cy="48" r="32" fill={A} />
      <path d="M18 48 h64 M22 34 h56 M22 62 h56 M30 22 h40 M30 74 h40 M50 16 v64 M36 18 v60 M64 18 v60" stroke={P} strokeWidth="1.6" opacity=".55" />
      <circle cx="50" cy="48" r="32" fill="none" stroke={B} strokeWidth="2.5" />
      <path d="M10 20 l10 10 M90 20 l-10 10 M8 70 l10 -6 M92 70 l-10 -6" stroke={G} strokeWidth="2.5" strokeLinecap="round" />
      <Spark x={14} y={44} s={0.5} fill={B} />
      <Spark x={86} y={44} s={0.5} fill={B} />
    </>
  ),

  groovy: () => (
    <>
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <ellipse key={deg} cx="50" cy="28" rx="9" ry="16" fill={A} transform={`rotate(${deg} 50 50)`} />
      ))}
      <circle cx="50" cy="50" r="11" fill={G} />
      <path d="M4 88 C 14 76, 22 100, 32 88 S 50 76, 60 88 S 78 100, 88 88" fill="none" stroke={B} strokeWidth="4" strokeLinecap="round" />
      <path d="M8 12 C 14 4, 20 20, 26 12" fill="none" stroke={B} strokeWidth="3" strokeLinecap="round" />
    </>
  ),

  popper: () => (
    <>
      <path d="M14 86 L 40 40 L 58 58 Z" fill={A} />
      <path d="M22 78 l24 -24 M18 82 l8 -8" stroke={P} strokeWidth="2" opacity=".6" />
      <path d="M52 40 C 58 26, 70 30, 72 18" fill="none" stroke={B} strokeWidth="3" strokeLinecap="round" />
      <path d="M60 50 C 72 46, 78 56, 90 50" fill="none" stroke={G} strokeWidth="3" strokeLinecap="round" />
      <path d="M46 30 C 44 20, 34 22, 32 12" fill="none" stroke={B} strokeWidth="3" strokeLinecap="round" />
      <path d="M70 66 l8 -4 2 8 -8 4z" fill={A} />
      <path d="M78 30 l7 -5 2 7 -7 5z" fill={G} />
      <Dot x={62} y={14} r={3} fill={B} />
      <Dot x={90} y={36} r={2.5} fill={A} />
      <Star x={84} y={80} s={0.5} fill={G} />
    </>
  ),

  balloon: () => (
    <>
      <path d="M32 10 C 18 10, 12 24, 14 36 C 16 48, 26 56, 32 60 C 38 56, 48 48, 50 36 C 52 24, 46 10, 32 10 Z" fill={A} />
      <path d="M68 6 C 56 6, 50 18, 52 30 C 54 42, 62 50, 68 54 C 74 50, 82 42, 84 30 C 86 18, 80 6, 68 6 Z" fill={B} />
      <path d="M52 24 C 42 24, 38 34, 40 44 C 42 52, 48 58, 52 62 C 56 58, 62 52, 64 44 C 66 34, 62 24, 52 24 Z" fill={G} />
      <path d="M32 60 l-2 4 4 0z M68 54 l-2 4 4 0z M52 62 l-2 4 4 0z" fill={I} opacity=".6" />
      <path d="M32 64 C 36 76, 28 84, 34 94 M68 58 C 62 72, 72 80, 64 94 M52 66 C 50 78, 56 86, 50 94" fill="none" stroke={I} strokeWidth="1.6" opacity=".6" />
      <path d="M22 20 c 4 -6, 10 -6, 12 -2" fill="none" stroke={P} strokeWidth="2" opacity=".7" />
    </>
  ),

  rings: () => (
    <>
      <circle cx="40" cy="56" r="22" fill="none" stroke={A} strokeWidth="5" />
      <circle cx="62" cy="48" r="22" fill="none" stroke={G} strokeWidth="5" />
      <path d="M62 10 l6 8 -6 8 -6 -8z" fill={G} />
      <path d="M58 18 h8" stroke={P} strokeWidth="1.2" opacity=".8" />
      <Spark x={18} y={22} s={0.5} fill={B} />
      <Spark x={88} y={80} s={0.5} fill={B} />
    </>
  ),

  eucalyptus: () => (
    <>
      <path d="M50 92 C 48 70, 50 40, 60 10" fill="none" stroke={B} strokeWidth="2.5" strokeLinecap="round" />
      {[[48, 78, -20], [56, 66, 20], [46, 56, -20], [58, 46, 20], [50, 36, -20], [60, 26, 20], [54, 16, -10]].map(([x, y, r], i) => (
        <ellipse key={i} cx={x} cy={y} rx="8" ry="6.5" fill={A} opacity={0.85 - i * 0.04} transform={`rotate(${r} ${x} ${y})`} />
      ))}
      <path d="M22 84 C 30 70, 40 66, 44 60" fill="none" stroke={B} strokeWidth="2" strokeLinecap="round" />
      <ellipse cx="26" cy="78" rx="6" ry="5" fill={A} opacity=".7" />
      <ellipse cx="36" cy="68" rx="6" ry="5" fill={A} opacity=".7" />
    </>
  ),

  rose: () => (
    <>
      <circle cx="50" cy="44" r="24" fill={A} />
      <path d="M50 44 m-14 0 a14 14 0 1 1 28 0 a10 10 0 1 1 -20 0 a6 6 0 1 1 12 0 a3 3 0 1 1 -6 0" fill="none" stroke={P} strokeWidth="2.2" opacity=".75" />
      <path d="M50 68 v24" stroke={B} strokeWidth="3" strokeLinecap="round" />
      <path d="M50 78 C 40 78, 32 72, 30 64 C 40 64, 48 70, 50 78 Z" fill={B} />
      <path d="M50 86 C 60 86, 68 80, 70 72 C 60 72, 52 78, 50 86 Z" fill={B} />
      <path d="M28 34 C 20 30, 18 22, 22 16 C 30 18, 32 26, 28 34 Z" fill={B} opacity=".8" />
      <path d="M74 36 C 82 32, 84 24, 80 18 C 72 20, 70 28, 74 36 Z" fill={B} opacity=".8" />
    </>
  ),

  flourish: () => (
    <>
      <path d="M50 50 C 40 30, 16 30, 12 46 C 10 56, 20 62, 28 56 C 34 52, 30 42, 22 46" fill="none" stroke={A} strokeWidth="2.6" strokeLinecap="round" />
      <path d="M50 50 C 60 30, 84 30, 88 46 C 90 56, 80 62, 72 56 C 66 52, 70 42, 78 46" fill="none" stroke={A} strokeWidth="2.6" strokeLinecap="round" />
      <path d="M50 50 C 44 62, 30 66, 22 74 M50 50 C 56 62, 70 66, 78 74" fill="none" stroke={A} strokeWidth="2" strokeLinecap="round" />
      <path d="M50 42 l5 8 -5 8 -5 -8z" fill={G} />
      <Dot x={22} y={74} r={2.2} fill={G} />
      <Dot x={78} y={74} r={2.2} fill={G} />
      <Dot x={50} y={26} r={2} fill={B} />
    </>
  ),

  wheat: () => (
    <>
      {[[30, -12], [50, 0], [70, 12]].map(([x, r], i) => (
        <g key={i} transform={`rotate(${r} ${x} 90)`}>
          <path d={`M${x} 92 V 40`} stroke={B} strokeWidth="2.5" strokeLinecap="round" />
          {[44, 52, 60, 68].map((y) => (
            <g key={y}>
              <ellipse cx={x - 5} cy={y} rx="3.2" ry="6" fill={A} transform={`rotate(-30 ${x - 5} ${y})`} />
              <ellipse cx={x + 5} cy={y} rx="3.2" ry="6" fill={A} transform={`rotate(30 ${x + 5} ${y})`} />
            </g>
          ))}
          <ellipse cx={x} cy={36} rx="3.2" ry="7" fill={A} />
        </g>
      ))}
      <path d="M22 84 C 40 76, 60 76, 78 84" fill="none" stroke={G} strokeWidth="3" strokeLinecap="round" />
    </>
  ),

  arch: () => (
    <>
      <path d="M14 92 V 50 A 22 22 0 0 1 58 50 V 92 Z" fill={A} />
      <path d="M36 92 V 58 A 14 14 0 0 1 64 58 V 92 Z" fill={B} />
      <path d="M50 92 V 66 A 8 8 0 0 1 66 66 V 92 Z" fill={G} />
      <circle cx="78" cy="24" r="10" fill={G} />
      <path d="M78 6 v6 M78 36 v6 M60 24 h6 M90 24 h6 M65 11 l4 4 M87 33 l4 4 M91 11 l-4 4 M69 33 l-4 4" stroke={G} strokeWidth="2" strokeLinecap="round" />
    </>
  ),

  'deco-fan': () => (
    <>
      <path d="M50 88 L 8 40 A 60 60 0 0 1 92 40 Z" fill={A} opacity=".85" />
      {[-60, -40, -20, 0, 20, 40, 60].map((deg) => (
        <path key={deg} d="M50 88 L 50 32" stroke={P} strokeWidth="1.6" opacity=".8" transform={`rotate(${deg} 50 88)`} />
      ))}
      <path d="M50 88 L 22 56 A 40 40 0 0 1 78 56 Z" fill={G} opacity=".9" />
      <Dot x={50} y={88} r={5} fill={I} />
      <Dot x={12} y={20} r={2.2} fill={G} />
      <Dot x={88} y={20} r={2.2} fill={G} />
    </>
  ),

  grapes: () => (
    <>
      <path d="M50 22 C 44 10, 32 8, 26 14 C 32 18, 40 22, 50 22 Z" fill={B} />
      <path d="M50 22 V 12" stroke={I} strokeWidth="2" strokeLinecap="round" />
      {[[38, 34], [50, 32], [62, 34], [32, 46], [44, 46], [56, 46], [68, 46], [38, 58], [50, 58], [62, 58], [44, 70], [56, 70], [50, 82]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="7.2" fill={A} opacity={0.95 - (i % 3) * 0.08} />
      ))}
      <Dot x={34} y={31} r={1.8} fill={P} o={0.6} />
      <Dot x={46} y={43} r={1.8} fill={P} o={0.6} />
    </>
  ),

  'wave-shell': () => (
    <>
      <path d="M50 74 C 30 74, 18 58, 22 40 L 78 40 C 82 58, 70 74, 50 74 Z" fill={A} />
      <path d="M22 40 C 30 24, 70 24, 78 40" fill={A} />
      <path d="M50 74 L 34 42 M50 74 L 44 40 M50 74 L 56 40 M50 74 L 66 42" stroke={P} strokeWidth="2" opacity=".6" />
      <path d="M4 90 q12 -8 24 0 q12 8 24 0 q12 -8 24 0 q12 8 20 0" fill="none" stroke={B} strokeWidth="3.5" strokeLinecap="round" />
      <Dot x={84} y={20} r={7} fill={G} />
    </>
  ),

  'line-leaf': () => (
    <>
      <path d="M20 88 C 30 60, 50 34, 84 12" fill="none" stroke={A} strokeWidth="1.8" strokeLinecap="round" />
      {[[30, 66, -30], [44, 46, -30], [60, 30, -30], [40, 74, 30], [54, 54, 30], [70, 36, 30]].map(([x, y, r], i) => (
        <path key={i} d="M0 0 C 6 -10, 16 -10, 22 0 C 16 10, 6 10, 0 0 Z" fill="none" stroke={A} strokeWidth="1.6" transform={`translate(${x} ${y}) rotate(${r})`} />
      ))}
    </>
  ),

  lavender: () => (
    <>
      {[[30, 6], [50, 0], [70, -6]].map(([x, r], i) => (
        <g key={i} transform={`rotate(${r} ${x} 92)`}>
          <path d={`M${x} 92 V 36`} stroke={B} strokeWidth="2.2" strokeLinecap="round" />
          {[22, 30, 38, 46, 54].map((y, j) => (
            <g key={y}>
              <ellipse cx={x - 4} cy={y + 2} rx="3.4" ry="5" fill={A} opacity={0.95 - j * 0.08} transform={`rotate(-35 ${x - 4} ${y})`} />
              <ellipse cx={x + 4} cy={y + 2} rx="3.4" ry="5" fill={A} opacity={0.95 - j * 0.08} transform={`rotate(35 ${x + 4} ${y})`} />
            </g>
          ))}
          <ellipse cx={x} cy={16} rx="3" ry="5" fill={A} />
        </g>
      ))}
      <path d="M50 74 C 42 72, 36 66, 34 60 C 42 60, 48 66, 50 74 Z" fill={B} opacity=".8" />
    </>
  ),

  diamond: () => (
    <>
      <path d="M30 30 h40 l16 20 -36 40 -36 -40 z" fill={A} />
      <path d="M30 30 l14 20 h12 l14 -20 M14 50 h72 M44 50 l6 40 M56 50 l-6 40" stroke={P} strokeWidth="2" opacity=".7" fill="none" />
      <path d="M30 30 l14 20 h-30 z M70 30 l-14 20 h30 z" fill={P} opacity=".25" />
      <Spark x={20} y={16} s={0.7} fill={G} />
      <Spark x={84} y={20} s={0.5} fill={G} />
      <Spark x={86} y={74} s={0.4} fill={B} />
    </>
  ),

  heart: () => (
    <>
      <path d="M50 88 C 24 66, 10 52, 10 34 C 10 20, 20 12, 32 12 C 40 12, 46 16, 50 24 C 54 16, 60 12, 68 12 C 80 12, 90 20, 90 34 C 90 52, 76 66, 50 88 Z" fill={A} />
      <path d="M24 30 C 24 24, 28 20, 34 20" fill="none" stroke={P} strokeWidth="2.5" strokeLinecap="round" opacity=".7" />
      <path d="M78 60 C 70 54, 66 50, 66 44 C 66 40, 69 38, 72 38 C 74 38, 76 39, 78 42 C 80 39, 82 38, 84 38 C 87 38, 90 40, 90 44 C 90 50, 86 54, 78 60 Z" fill={B} />
      <Dot x={14} y={78} r={2.6} fill={B} />
    </>
  ),

  'cloud-moon': () => (
    <>
      <path d="M72 8 A 26 26 0 1 0 92 44 A 20 20 0 1 1 72 8 Z" fill={B} />
      <path d="M12 82 a12 12 0 0 1 10 -18 a16 16 0 0 1 30 2 a11 11 0 0 1 6 16 z" fill={P} stroke={A} strokeWidth="2.5" />
      <path d="M50 66 a9 9 0 0 1 8 -12 a12 12 0 0 1 22 4 a8 8 0 0 1 2 8 z" fill={P} stroke={A} strokeWidth="2" opacity=".9" />
      <Star x={18} y={26} s={0.6} fill={A} />
      <Star x={40} y={40} s={0.4} fill={A} />
      <Dot x={30} y={14} r={1.6} fill={A} />
    </>
  ),

  blossom: () => (
    <>
      <path d="M10 90 C 30 70, 50 50, 90 14" fill="none" stroke={I} strokeWidth="2.5" strokeLinecap="round" opacity=".7" />
      <path d="M40 60 C 44 50, 52 46, 60 46 M62 38 C 66 30, 74 28, 80 30" fill="none" stroke={I} strokeWidth="2" strokeLinecap="round" opacity=".6" />
      {[[30, 66, 1], [58, 44, 1.1], [80, 28, 0.9], [48, 78, 0.7], [72, 54, 0.6]].map(([x, y, s], i) => (
        <g key={i} transform={`translate(${x} ${y}) scale(${s})`}>
          {[0, 72, 144, 216, 288].map((deg) => (
            <ellipse key={deg} cx="0" cy="-7" rx="4.2" ry="6.5" fill={A} transform={`rotate(${deg})`} />
          ))}
          <circle r="3" fill={G} />
        </g>
      ))}
      <Dot x={20} y={30} r={2} fill={A} o={0.6} />
    </>
  ),

  'balloons-duo': () => (
    <>
      <path d="M34 8 C 20 8, 14 22, 16 34 C 18 46, 28 54, 34 58 C 40 54, 50 46, 52 34 C 54 22, 48 8, 34 8 Z" fill={A} />
      <path d="M68 14 C 54 14, 48 28, 50 40 C 52 52, 62 60, 68 64 C 74 60, 84 52, 86 40 C 88 28, 82 14, 68 14 Z" fill={B} />
      <path d="M34 58 l-2 4 4 0z M68 64 l-2 4 4 0z" fill={I} opacity=".6" />
      <path d="M34 62 C 38 74, 30 82, 36 94 M68 68 C 62 78, 72 86, 64 94" fill="none" stroke={I} strokeWidth="1.6" opacity=".6" />
      <text x="34" y="40" textAnchor="middle" fontSize="22" fontWeight="700" fill={P} fontFamily="system-ui, sans-serif">?</text>
      <text x="68" y="46" textAnchor="middle" fontSize="22" fontWeight="700" fill={P} fontFamily="system-ui, sans-serif">?</text>
    </>
  ),

  'mushroom-fox': () => (
    <>
      <path d="M8 52 C 8 32, 26 22, 40 22 C 54 22, 72 32, 72 52 Z" fill={A} />
      <rect x="30" y="52" width="20" height="30" rx="6" fill={P} stroke={A} strokeWidth="2" />
      <circle cx="24" cy="38" r="4.5" fill={P} />
      <circle cx="42" cy="32" r="3.5" fill={P} />
      <circle cx="58" cy="42" r="4" fill={P} />
      <path d="M62 92 L 66 62 L 76 74 L 88 62 L 92 92 Z" fill={B} />
      <path d="M72 92 C 74 86, 80 86, 82 92 Z" fill={P} />
      <circle cx="73" cy="80" r="1.8" fill={I} />
      <circle cx="83" cy="80" r="1.8" fill={I} />
      <circle cx="78" cy="87" r="1.6" fill={I} />
      <Dot x={16} y={84} r={3} fill={G} />
    </>
  ),

  lion: () => (
    <>
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => (
        <ellipse key={deg} cx="50" cy="16" rx="9" ry="14" fill={A} transform={`rotate(${deg} 50 50)`} />
      ))}
      <circle cx="50" cy="50" r="24" fill={G} />
      <circle cx="42" cy="46" r="2.6" fill={I} />
      <circle cx="58" cy="46" r="2.6" fill={I} />
      <path d="M46 56 l4 3 4 -3 z" fill={I} />
      <path d="M50 59 v4 M44 65 q6 5 12 0" stroke={I} strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <circle cx="34" cy="40" r="6" fill={G} />
      <circle cx="66" cy="40" r="6" fill={G} />
    </>
  ),

  donut: () => (
    <>
      <circle cx="50" cy="50" r="34" fill={G} />
      <path d="M50 16 C 30 16, 16 30, 16 50 C 24 44, 32 52, 40 46 C 48 40, 56 54, 64 48 C 72 42, 78 52, 84 46 C 82 30, 68 16, 50 16 Z" fill={A} />
      <circle cx="50" cy="50" r="11" fill={P} />
      <path d="M32 36 l4 -3 M60 30 l3 4 M74 42 l4 -2 M40 28 l3 3" stroke={B} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M26 66 l4 2 M44 76 l4 -1 M68 70 l3 3" stroke={P} strokeWidth="2.5" strokeLinecap="round" opacity=".8" />
    </>
  ),

  star: () => (
    <>
      <Star x={50} y={48} s={3.4} fill={A} />
      <Star x={18} y={22} s={0.9} fill={G} />
      <Star x={84} y={24} s={0.7} fill={G} />
      <Star x={82} y={80} s={0.8} fill={B} />
      <Star x={16} y={80} s={0.55} fill={B} />
      <Dot x={50} y={92} r={1.6} fill={G} />
    </>
  ),

  dove: () => (
    <>
      <path d="M26 56 C 30 40, 48 36, 62 42 C 74 48, 80 60, 78 70 C 70 66, 60 66, 50 72 C 40 78, 30 74, 26 56 Z" fill={A} />
      <path d="M50 44 C 40 26, 48 12, 66 14 C 60 24, 62 34, 56 44 Z" fill={A} opacity=".85" />
      <circle cx="66" cy="50" r="1.8" fill={I} />
      <path d="M76 52 l8 2 -8 2z" fill={G} />
      <path d="M78 70 C 84 74, 86 80, 84 88" fill="none" stroke={A} strokeWidth="2" strokeLinecap="round" />
      <path d="M10 84 C 18 74, 30 74, 40 82" fill="none" stroke={B} strokeWidth="2" strokeLinecap="round" />
      {[[16, 80, -20], [24, 76, -10], [32, 76, 10]].map(([x, y, r], i) => (
        <ellipse key={i} cx={x} cy={y} rx="3" ry="5.5" fill={B} transform={`rotate(${r} ${x} ${y})`} />
      ))}
    </>
  ),

  briefcase: () => (
    <>
      <rect x="14" y="36" width="72" height="46" rx="6" fill={A} />
      <path d="M38 36 v-8 a4 4 0 0 1 4 -4 h16 a4 4 0 0 1 4 4 v8" fill="none" stroke={A} strokeWidth="5" />
      <rect x="14" y="52" width="72" height="4" fill={P} opacity=".5" />
      <rect x="44" y="50" width="12" height="9" rx="2" fill={G} />
      <path d="M8 18 l6 -6 M20 8 l2 6 M84 12 l-6 4 M92 26 l-6 -2" stroke={B} strokeWidth="3" strokeLinecap="round" />
      <Dot x={12} y={30} r={2.2} fill={G} />
      <Dot x={90} y={40} r={2} fill={B} />
    </>
  ),

  champagne: () => (
    <>
      <path d="M28 12 h16 l-2 30 a6 6 0 0 1 -12 0 z" fill={A} opacity=".9" />
      <path d="M36 48 v28 M28 78 h16" stroke={A} strokeWidth="3" strokeLinecap="round" />
      <path d="M56 12 h16 l-2 30 a6 6 0 0 1 -12 0 z" fill={A} opacity=".9" />
      <path d="M64 48 v28 M56 78 h16" stroke={A} strokeWidth="3" strokeLinecap="round" />
      <path d="M30 22 h12 M58 22 h12" stroke={P} strokeWidth="2" opacity=".5" />
      <Dot x={34} y={30} r={1.6} fill={P} o={0.7} />
      <Dot x={38} y={36} r={1.2} fill={P} o={0.7} />
      <Dot x={64} y={32} r={1.6} fill={P} o={0.7} />
      <Spark x={50} y={10} s={0.6} fill={G} />
      <Dot x={50} y={26} r={1.6} fill={G} />
      <Dot x={16} y={60} r={2} fill={G} />
      <Dot x={84} y={62} r={2} fill={G} />
    </>
  ),

  lightbulb: () => (
    <>
      <path d="M50 14 C 32 14, 22 28, 26 42 C 28 50, 34 54, 38 62 H 62 C 66 54, 72 50, 74 42 C 78 28, 68 14, 50 14 Z" fill={G} />
      <rect x="38" y="64" width="24" height="7" rx="2" fill={I} />
      <rect x="40" y="73" width="20" height="6" rx="2" fill={I} opacity=".8" />
      <path d="M44 62 l3 -14 h6 l3 14" fill="none" stroke={P} strokeWidth="2" />
      <path d="M50 2 v6 M20 10 l4 4 M80 10 l-4 4 M10 40 h6 M84 40 h6" stroke={A} strokeWidth="3" strokeLinecap="round" />
      <Spark x={86} y={72} s={0.5} fill={B} />
    </>
  ),

  globe: () => (
    <>
      <circle cx="50" cy="50" r="34" fill="none" stroke={A} strokeWidth="3" />
      <ellipse cx="50" cy="50" rx="14" ry="34" fill="none" stroke={A} strokeWidth="2" />
      <path d="M16 50 h68 M22 32 h56 M22 68 h56" stroke={A} strokeWidth="2" />
      <path d="M50 16 v68" stroke={A} strokeWidth="2" />
      <Dot x={36} y={40} r={4} fill={B} />
      <Dot x={64} y={58} r={4} fill={B} />
      <path d="M36 40 C 46 44, 56 52, 64 58" fill="none" stroke={B} strokeWidth="1.5" strokeDasharray="3 3" />
      <Dot x={86} y={16} r={2.4} fill={G} />
    </>
  ),

  puzzle: () => (
    <>
      <path d="M20 26 h20 a8 8 0 0 1 16 0 h20 v20 a8 8 0 0 0 0 16 v20 H 56 a8 8 0 0 1 -16 0 H 20 V 62 a8 8 0 0 1 0 -16 Z" fill={A} />
      <path d="M62 8 a6 6 0 0 1 12 0 h14 v14 a6 6 0 0 0 0 12 v6" fill="none" stroke={B} strokeWidth="3" strokeLinecap="round" />
      <Dot x={12} y={86} r={4} fill={G} />
      <Dot x={90} y={90} r={2.5} fill={B} />
    </>
  ),

  trophy: () => (
    <>
      <path d="M30 14 h40 v22 a20 20 0 0 1 -40 0 Z" fill={G} />
      <path d="M30 20 h-10 a10 10 0 0 0 10 16 M70 20 h10 a10 10 0 0 1 -10 16" fill="none" stroke={G} strokeWidth="4" strokeLinecap="round" />
      <path d="M46 56 h8 v10 h-8z" fill={I} />
      <rect x="34" y="66" width="32" height="8" rx="2" fill={A} />
      <rect x="30" y="74" width="40" height="6" rx="2" fill={A} opacity=".8" />
      <Star x={50} y={30} s={0.8} fill={P} />
      <Spark x={14} y={54} s={0.5} fill={B} />
      <Spark x={88} y={60} s={0.5} fill={B} />
    </>
  ),

  sailboat: () => (
    <>
      <path d="M50 12 V 62" stroke={I} strokeWidth="2.5" />
      <path d="M52 16 C 74 24, 78 46, 74 60 H 52 Z" fill={A} />
      <path d="M48 24 C 30 30, 26 46, 30 60 H 48 Z" fill={B} />
      <path d="M18 64 h64 l-8 14 H 26 Z" fill={I} />
      <path d="M4 88 q12 -8 24 0 q12 8 24 0 q12 -8 24 0 q12 8 20 0" fill="none" stroke={A} strokeWidth="3" strokeLinecap="round" opacity=".7" />
      <Dot x={86} y={18} r={8} fill={G} />
    </>
  ),

  circuit: () => (
    <>
      <path d="M10 30 h30 v20 h20 v-20 h30 M20 70 h20 v-20 M60 70 h30 M50 80 v-30" fill="none" stroke={A} strokeWidth="2.5" strokeLinecap="round" />
      <Dot x={10} y={30} r={4} fill={B} />
      <Dot x={90} y={30} r={4} fill={B} />
      <Dot x={20} y={70} r={4} fill={B} />
      <Dot x={90} y={70} r={4} fill={B} />
      <Dot x={50} y={80} r={4} fill={G} />
      <rect x="42" y="42" width="16" height="16" rx="3" fill={A} />
      <rect x="46" y="46" width="8" height="8" fill={P} opacity=".7" />
    </>
  ),

  'ribbon-heart': () => (
    <>
      <path d="M50 84 C 26 64, 12 50, 12 32 C 12 18, 22 10, 34 10 C 42 10, 48 14, 50 22 C 52 14, 58 10, 66 10 C 78 10, 88 18, 88 32 C 88 50, 74 64, 50 84 Z" fill="none" stroke={A} strokeWidth="6" strokeLinejoin="round" />
      <path d="M40 40 C 48 52, 52 60, 50 84 M60 40 C 52 52, 48 60, 50 84" fill="none" stroke={G} strokeWidth="4" strokeLinecap="round" />
      <path d="M40 40 a6 6 0 1 1 20 0 a10 10 0 0 1 -20 0 z" fill={G} />
    </>
  ),

  fireworks: () => (
    <>
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
        <path key={deg} d="M50 50 L 50 14" stroke={A} strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${deg} 50 50)`} />
      ))}
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
        <circle key={deg} cx="50" cy="10" r="3" fill={B} transform={`rotate(${deg + 22} 50 50)`} />
      ))}
      <Dot x={50} y={50} r={4} fill={G} />
      <Spark x={14} y={80} s={0.9} fill={G} />
      <Spark x={86} y={82} s={0.6} fill={G} />
      <Dot x={14} y={20} r={1.6} fill={B} />
    </>
  ),

  pine: () => (
    <>
      <path d="M14 86 C 30 66, 50 44, 84 14" fill="none" stroke={B} strokeWidth="3" strokeLinecap="round" />
      {[[22, 78], [32, 66], [44, 54], [56, 42], [68, 30]].map(([x, y], i) => (
        <g key={i}>
          <path d={`M${x} ${y} l-14 -2 M${x} ${y} l-10 -12 M${x} ${y} l2 -14`} stroke={B} strokeWidth="2.2" strokeLinecap="round" />
          <path d={`M${x} ${y} l12 -8 M${x} ${y} l14 2`} stroke={B} strokeWidth="2.2" strokeLinecap="round" />
        </g>
      ))}
      <circle cx="78" cy="70" r="11" fill={A} />
      <path d="M78 59 v-6 M74 53 h8" stroke={G} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M70 68 C 74 62, 82 62, 86 68" fill="none" stroke={P} strokeWidth="2" opacity=".6" />
      <Star x={24} y={22} s={0.6} fill={G} />
    </>
  ),

  snowflake: () => (
    <>
      {[0, 60, 120].map((deg) => (
        <g key={deg} transform={`rotate(${deg} 50 50)`}>
          <path d="M50 8 V 92" stroke={A} strokeWidth="3" strokeLinecap="round" />
          <path d="M50 20 l-8 -8 M50 20 l8 -8 M50 32 l-9 -6 M50 32 l9 -6 M50 80 l-8 8 M50 80 l8 8 M50 68 l-9 6 M50 68 l9 6" stroke={A} strokeWidth="2.4" strokeLinecap="round" />
        </g>
      ))}
      <circle cx="50" cy="50" r="5" fill={B} />
      <Dot x={14} y={16} r={2} fill={B} />
      <Dot x={88} y={84} r={2.5} fill={B} />
    </>
  ),

  'pumpkin-ghost': () => (
    <>
      <path d="M30 40 C 14 40, 10 60, 16 74 C 20 84, 30 88, 40 86 C 46 88, 54 88, 60 86 C 70 88, 80 84, 84 74 C 90 60, 86 40, 70 40 C 62 36, 38 36, 30 40 Z" fill={A} />
      <path d="M40 40 C 36 54, 36 70, 40 86 M60 40 C 64 54, 64 70, 60 86" stroke={I} strokeWidth="1.6" opacity=".3" fill="none" />
      <path d="M48 40 C 46 32, 48 26, 54 22" stroke={B} strokeWidth="4" strokeLinecap="round" fill="none" />
      <path d="M36 56 l6 8 h-12z M64 56 l6 8 h-12z M36 72 h28 l-5 6 h-4 l-3 -4 -3 4 h-4 l-3 -4 -3 4 z" fill={I} />
      <path d="M76 8 C 66 8, 62 16, 62 24 V 40 l4 -3 4 3 4 -3 4 3 4 -3 4 3 V 24 C 90 16, 86 8, 76 8 Z" fill={P} stroke={I} strokeWidth="1.5" />
      <circle cx="72" cy="20" r="2" fill={I} />
      <circle cx="80" cy="20" r="2" fill={I} />
    </>
  ),

  'egg-bunny': () => (
    <>
      <path d="M32 44 C 32 28, 40 18, 50 18 C 60 18, 68 28, 68 44 C 68 58, 60 66, 50 66 C 40 66, 32 58, 32 44 Z" fill={A} />
      <path d="M34 36 q8 4 16 0 q8 -4 16 0 M33 50 q8 4 17 0 q8 -4 16 0" fill="none" stroke={P} strokeWidth="2.5" strokeLinecap="round" opacity=".8" />
      <path d="M10 82 C 10 66, 18 58, 28 58 C 38 58, 46 66, 46 82 Z" fill={B} />
      <path d="M62 82 C 62 66, 70 58, 80 58 C 90 58, 98 66, 98 82 Z" fill={G} />
      <path d="M18 60 C 14 48, 16 36, 22 30 C 26 40, 26 50, 24 60 Z M34 60 C 30 48, 32 36, 38 30 C 42 40, 42 50, 40 60 Z" fill={P} stroke={I} strokeWidth="1.5" />
      <circle cx="24" cy="68" r="1.8" fill={I} />
      <circle cx="34" cy="68" r="1.8" fill={I} />
      <path d="M26 74 q3 3 6 0" stroke={I} strokeWidth="1.4" fill="none" strokeLinecap="round" />
    </>
  ),

  'sun-wave': () => (
    <>
      <circle cx="50" cy="40" r="18" fill={A} />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
        <path key={deg} d="M50 12 v-8" stroke={A} strokeWidth="4" strokeLinecap="round" transform={`rotate(${deg} 50 40)`} />
      ))}
      <path d="M4 74 q12 -10 24 0 q12 10 24 0 q12 -10 24 0 q12 10 20 0" fill="none" stroke={B} strokeWidth="5" strokeLinecap="round" />
      <path d="M4 88 q12 -10 24 0 q12 10 24 0 q12 -10 24 0 q12 10 20 0" fill="none" stroke={B} strokeWidth="4" strokeLinecap="round" opacity=".6" />
    </>
  ),

  'autumn-leaf': () => (
    <>
      <path d="M50 90 V 50" stroke={I} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M50 52 L 30 60 L 36 46 L 18 40 L 34 34 L 26 18 L 42 26 L 50 8 L 58 26 L 74 18 L 66 34 L 82 40 L 64 46 L 70 60 Z" fill={A} />
      <path d="M50 52 L 50 12 M50 52 L 30 24 M50 52 L 70 24" stroke={P} strokeWidth="1.5" opacity=".5" />
      <ellipse cx="84" cy="80" rx="8" ry="10" fill={B} />
      <path d="M74 74 a10 6 0 0 1 20 0 z" fill={I} opacity=".7" />
      <path d="M84 64 v-4" stroke={I} strokeWidth="2" strokeLinecap="round" />
    </>
  ),

  tulip: () => (
    <>
      {[[28, 0], [50, 0], [72, 0]].map(([x], i) => (
        <g key={i}>
          <path d={`M${x} 92 V 46`} stroke={B} strokeWidth="2.5" strokeLinecap="round" />
          <path d={`M${x - 12} 34 C ${x - 12} 22, ${x - 6} 14, ${x} 12 C ${x + 6} 14, ${x + 12} 22, ${x + 12} 34 C ${x + 12} 44, ${x + 4} 50, ${x} 50 C ${x - 4} 50, ${x - 12} 44, ${x - 12} 34 Z`} fill={A} />
          <path d={`M${x - 12} 34 L ${x - 4} 24 L ${x} 34 L ${x + 4} 24 L ${x + 12} 34`} fill="none" stroke={P} strokeWidth="1.6" opacity=".7" />
          <path d={`M${x} 70 C ${x - 8} 70, ${x - 14} 64, ${x - 14} 56 C ${x - 6} 56, ${x} 62, ${x} 70 Z`} fill={B} />
        </g>
      ))}
      <Dot x={12} y={16} r={3} fill={G} />
      <Dot x={88} y={20} r={3} fill={G} />
      <Dot x={90} y={64} r={2.5} fill={G} />
    </>
  ),

  churchkhela: () => (
    <>
      {[[30, 0], [50, 4], [70, 0]].map(([x, dy], i) => (
        <g key={i}>
          <path d={`M${x} ${8 + dy} v6`} stroke={I} strokeWidth="2" strokeLinecap="round" />
          <path d={`M${x - 7} ${14 + dy} h14 v52 a7 7 0 0 1 -14 0 z`} fill={A} />
          {[24, 36, 48, 60].map((y) => (
            <circle key={y} cx={x} cy={y + dy} r="7.5" fill={A} stroke={P} strokeWidth="1.2" opacity=".95" />
          ))}
        </g>
      ))}
      <path d="M10 92 C 20 84, 30 84, 40 92" fill="none" stroke={B} strokeWidth="2" strokeLinecap="round" />
      <circle cx="88" cy="84" r="5" fill={B} />
      <circle cx="80" cy="90" r="4" fill={B} />
      <circle cx="90" cy="93" r="3.5" fill={B} />
    </>
  ),

  qvevri: () => (
    <>
      <path d="M30 20 h40 v10 C 80 40, 84 60, 74 80 C 70 88, 60 92, 50 92 C 40 92, 30 88, 26 80 C 16 60, 20 40, 30 30 Z" fill={A} />
      <path d="M34 20 h32 v6 H34 z" fill={I} opacity=".6" />
      <path d="M28 44 C 40 40, 60 40, 72 44" fill="none" stroke={P} strokeWidth="2" opacity=".5" />
      <path d="M78 22 C 90 16, 96 30, 88 44 C 84 52, 84 62, 88 70" fill="none" stroke={G} strokeWidth="5" strokeLinecap="round" />
      <path d="M78 22 C 84 20, 88 24, 86 30" fill={B} />
      <circle cx="12" cy="30" r="5" fill={B} />
      <circle cx="8" cy="40" r="4.5" fill={B} />
      <circle cx="16" cy="42" r="4" fill={B} />
    </>
  ),

  wineglass: () => (
    <>
      <path d="M32 10 h36 c2 20, -6 34, -18 36 c-12 -2, -20 -16, -18 -36 z" fill={P} stroke={A} strokeWidth="2.5" />
      <path d="M32 30 C 34 40, 42 46, 50 46 C 58 46, 66 40, 68 30 Z" fill={A} />
      <path d="M50 46 v34 M34 82 h32" stroke={A} strokeWidth="3.5" strokeLinecap="round" />
      <circle cx="82" cy="72" r="5" fill={B} />
      <circle cx="90" cy="80" r="4.5" fill={B} />
      <circle cx="80" cy="84" r="4" fill={B} />
      <circle cx="88" cy="90" r="3.5" fill={B} />
      <path d="M82 66 C 84 60, 90 60, 92 64" fill="none" stroke={I} strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),

  croissant: () => (
    <>
      <path d="M12 60 C 8 44, 22 30, 40 34 C 48 22, 68 22, 76 34 C 92 34, 96 52, 88 62 C 84 70, 74 68, 68 62 C 60 70, 40 70, 32 62 C 26 68, 14 70, 12 60 Z" fill={G} />
      <path d="M32 62 C 36 50, 40 42, 40 34 M68 62 C 64 50, 60 42, 60 34 M50 66 C 50 54, 50 44, 50 32" fill="none" stroke={A} strokeWidth="2" strokeLinecap="round" opacity=".7" />
      <path d="M60 78 h26 v10 a8 8 0 0 1 -8 8 h-10 a8 8 0 0 1 -8 -8 z" fill={P} stroke={I} strokeWidth="2" />
      <path d="M86 82 a5 5 0 0 1 0 10" fill="none" stroke={I} strokeWidth="2" />
      <path d="M66 74 c 0 -4, 3 -4, 3 -8 M73 74 c 0 -4, 3 -4, 3 -8" fill="none" stroke={B} strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),

  grill: () => (
    <>
      <path d="M14 46 h72 a36 20 0 0 1 -72 0 z" fill={I} />
      <path d="M14 46 h72" stroke={B} strokeWidth="4" />
      <path d="M30 66 l-8 24 M70 66 l8 24" stroke={I} strokeWidth="3.5" strokeLinecap="round" />
      <path d="M40 40 C 36 30, 44 26, 42 16 C 50 22, 46 30, 52 36 C 54 26, 62 26, 60 14 C 70 22, 66 34, 60 40 Z" fill={A} />
      <path d="M46 40 C 44 34, 48 30, 48 24 C 52 28, 50 34, 54 40 Z" fill={G} />
      <path d="M24 30 h12 M64 30 h12" stroke={B} strokeWidth="3" strokeLinecap="round" />
      <circle cx="18" cy="30" r="2.5" fill={B} />
      <circle cx="82" cy="30" r="2.5" fill={B} />
    </>
  ),

  mic: () => (
    <>
      <rect x="38" y="10" width="24" height="44" rx="12" fill={A} />
      <path d="M40 22 h20 M40 30 h20 M40 38 h20" stroke={P} strokeWidth="1.6" opacity=".5" />
      <path d="M28 44 a22 22 0 0 0 44 0" fill="none" stroke={I} strokeWidth="3.5" strokeLinecap="round" />
      <path d="M50 66 v14 M38 82 h24" stroke={I} strokeWidth="3.5" strokeLinecap="round" />
      <path d="M80 20 v20 a5 5 0 1 1 -3 -4.6 V 14 l10 -2 v6 z" fill={B} />
      <path d="M16 30 v14 a4 4 0 1 1 -2.5 -3.7 V 26 l8 -2 v5 z" fill={G} />
    </>
  ),

  popcorn: () => (
    <>
      <path d="M26 40 h48 l-6 48 H 32 Z" fill={A} />
      <path d="M36 40 l3 48 M50 40 v48 M64 40 l-3 48" stroke={P} strokeWidth="4" opacity=".85" />
      <circle cx="34" cy="38" r="8" fill={G} />
      <circle cx="50" cy="32" r="9" fill={G} />
      <circle cx="66" cy="38" r="8" fill={G} />
      <circle cx="42" cy="28" r="6" fill={P} />
      <circle cx="58" cy="26" r="6" fill={P} />
      <path d="M8 10 h22 v14 H8 z" fill={I} />
      <rect x="11" y="13" width="4" height="3" fill={P} /><rect x="18" y="13" width="4" height="3" fill={P} /><rect x="25" y="13" width="3" height="3" fill={P} />
      <rect x="11" y="18" width="4" height="3" fill={P} /><rect x="18" y="18" width="4" height="3" fill={P} /><rect x="25" y="18" width="3" height="3" fill={P} />
      <Star x={86} y={70} s={0.6} fill={B} />
    </>
  ),

  dice: () => (
    <>
      <rect x="16" y="30" width="40" height="40" rx="8" fill={A} transform="rotate(-12 36 50)" />
      {[[26, 40], [36, 50], [46, 60], [46, 40], [26, 60]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3.5" fill={P} transform="rotate(-12 36 50)" />
      ))}
      <rect x="54" y="44" width="34" height="34" rx="7" fill={B} transform="rotate(10 71 61)" />
      <circle cx="62" cy="52" r="3" fill={P} transform="rotate(10 71 61)" />
      <circle cx="80" cy="70" r="3" fill={P} transform="rotate(10 71 61)" />
      <path d="M70 12 a8 8 0 0 1 8 8 c0 4, -3 6, -3 10 h-10 c0 -4, -3 -6, -3 -10 a8 8 0 0 1 8 -8 z" fill={G} />
    </>
  ),

  cards: () => (
    <>
      <rect x="22" y="18" width="36" height="52" rx="5" fill={P} stroke={I} strokeWidth="2" transform="rotate(-10 40 44)" />
      <path d="M40 34 C 34 26, 26 32, 32 40 C 36 44, 40 48, 40 48 C 40 48, 44 44, 48 40 C 54 32, 46 26, 40 34 Z" fill={A} transform="rotate(-10 40 44)" />
      <rect x="46" y="24" width="36" height="52" rx="5" fill={P} stroke={I} strokeWidth="2" transform="rotate(8 64 50)" />
      <path d="M64 36 l10 14 -10 14 -10 -14 z" fill={I} transform="rotate(8 64 50)" />
      <circle cx="24" cy="86" r="8" fill={G} />
      <circle cx="24" cy="86" r="4" fill="none" stroke={P} strokeWidth="1.5" strokeDasharray="2 2" />
      <circle cx="40" cy="90" r="7" fill={B} />
      <circle cx="40" cy="90" r="3.5" fill="none" stroke={P} strokeWidth="1.5" strokeDasharray="2 2" />
    </>
  ),

  mask: () => (
    <>
      <path d="M10 40 C 20 26, 40 24, 50 34 C 60 24, 80 26, 90 40 C 92 56, 82 70, 68 68 C 58 68, 54 60, 50 56 C 46 60, 42 68, 32 68 C 18 70, 8 56, 10 40 Z" fill={A} />
      <ellipse cx="32" cy="46" rx="9" ry="5.5" fill={P} />
      <ellipse cx="68" cy="46" rx="9" ry="5.5" fill={P} />
      <path d="M50 30 C 46 18, 54 12, 50 4" fill="none" stroke={G} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M20 30 C 14 20, 18 12, 12 6 M80 30 C 86 20, 82 12, 88 6" fill="none" stroke={G} strokeWidth="2" strokeLinecap="round" />
      <path d="M90 42 C 96 54, 92 70, 84 84" fill="none" stroke={B} strokeWidth="2.5" strokeLinecap="round" />
      <Dot x={50} y={62} r={2.6} fill={G} />
    </>
  ),

  house: () => (
    <>
      <path d="M22 88 V 50 L 50 24 L 78 50 V 88 Z" fill={A} />
      <path d="M14 52 L 50 18 L 86 52" fill="none" stroke={I} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="42" y="62" width="16" height="26" rx="2" fill={P} />
      <rect x="28" y="56" width="10" height="10" rx="1.5" fill={P} opacity=".85" />
      <rect x="62" y="56" width="10" height="10" rx="1.5" fill={P} opacity=".85" />
      <rect x="62" y="26" width="7" height="14" fill={I} />
      <circle cx="86" cy="80" r="7" fill="none" stroke={G} strokeWidth="3.5" />
      <path d="M91 75 l8 -8 M96 70 l3 3 M93 73 l3 3" stroke={G} strokeWidth="3" strokeLinecap="round" />
    </>
  ),

  teacup: () => (
    <>
      <path d="M22 44 h52 v14 a26 20 0 0 1 -52 0 z" fill={P} stroke={A} strokeWidth="2.5" />
      <path d="M74 48 h8 a8 8 0 0 1 0 16 h-8" fill="none" stroke={A} strokeWidth="2.5" />
      <path d="M22 44 h52" stroke={A} strokeWidth="2.5" />
      <ellipse cx="48" cy="82" rx="30" ry="5" fill={A} opacity=".7" />
      <path d="M36 34 c 0 -6, 4 -6, 4 -12 M48 32 c 0 -6, 4 -6, 4 -12 M60 34 c 0 -6, 4 -6, 4 -12" fill="none" stroke={B} strokeWidth="2" strokeLinecap="round" />
      <circle cx="30" cy="54" r="4" fill={B} opacity=".8" />
      <circle cx="42" cy="58" r="3" fill={B} opacity=".8" />
      <circle cx="58" cy="56" r="3.5" fill={B} opacity=".8" />
    </>
  ),

  mountain: () => (
    <>
      <path d="M4 84 L 34 30 L 50 56 L 62 40 L 96 84 Z" fill={A} />
      <path d="M34 30 L 26 44 L 32 42 L 36 48 L 42 42 L 46 48 Z" fill={P} />
      <path d="M62 40 L 56 50 L 60 48 L 64 52 L 68 48 L 72 54 Z" fill={P} />
      <circle cx="78" cy="22" r="8" fill={G} />
      <path d="M10 84 l6 -14 6 14 M18 84 l6 -18 6 18" fill={B} />
      <path d="M4 88 h92" stroke={B} strokeWidth="2.5" strokeLinecap="round" opacity=".6" />
    </>
  ),

  tent: () => (
    <>
      <path d="M8 82 L 50 22 L 92 82 Z" fill={A} />
      <path d="M50 22 L 36 82 H 64 Z" fill={I} opacity=".8" />
      <path d="M50 22 L 36 82 M50 22 L 64 82" stroke={P} strokeWidth="1.5" opacity=".5" />
      <path d="M50 22 l-4 -8 M50 22 l4 -8" stroke={I} strokeWidth="2" strokeLinecap="round" />
      <path d="M78 76 C 74 66, 82 62, 80 52 C 88 58, 84 66, 90 70 C 92 60, 100 60, 98 50 C 104 60, 100 74, 92 78 Z" fill={G} transform="translate(-6 8)" />
      <path d="M70 90 l24 -6 M70 84 l24 6" stroke={B} strokeWidth="2.5" strokeLinecap="round" />
      <Star x={16} y={20} s={0.7} fill={G} />
      <Star x={86} y={14} s={0.5} fill={G} />
      <Dot x={30} y={10} r={1.6} fill={G} />
    </>
  ),

  umbrella: () => (
    <>
      <path d="M50 12 C 26 12, 10 30, 10 48 H 90 C 90 30, 74 12, 50 12 Z" fill={A} />
      <path d="M26 48 C 26 30, 36 16, 50 12 C 42 22, 40 36, 40 48 Z M60 48 C 60 36, 58 22, 50 12 C 64 16, 74 30, 74 48 Z" fill={P} opacity=".85" />
      <path d="M50 48 V 92" stroke={I} strokeWidth="3" strokeLinecap="round" transform="rotate(8 50 48)" />
      <circle cx="82" cy="70" r="9" fill={G} />
      <path d="M4 92 q10 -6 20 0 q10 6 20 0 q10 -6 20 0 q10 6 20 0" fill="none" stroke={B} strokeWidth="3" strokeLinecap="round" opacity=".7" />
    </>
  ),

  'watering-can': () => (
    <>
      <path d="M24 44 h40 v36 a6 6 0 0 1 -6 6 H 30 a6 6 0 0 1 -6 -6 z" fill={A} />
      <path d="M36 44 v-8 h16 v8" fill="none" stroke={A} strokeWidth="4" />
      <path d="M64 56 L 88 36 L 92 44 L 68 64 Z" fill={A} />
      <circle cx="90" cy="40" r="6" fill={B} />
      <path d="M24 56 a12 14 0 0 0 0 20" fill="none" stroke={A} strokeWidth="4" />
      <path d="M92 48 l2 8 M96 44 l4 6 M86 50 l0 8" stroke={B} strokeWidth="2" strokeLinecap="round" />
      {[[12, 78], [20, 84]].map(([x, y], i) => (
        <g key={i} transform={`translate(${x} ${y}) scale(.7)`}>
          {[0, 72, 144, 216, 288].map((deg) => (
            <ellipse key={deg} cx="0" cy="-7" rx="4" ry="6" fill={B} transform={`rotate(${deg})`} />
          ))}
          <circle r="2.6" fill={G} />
        </g>
      ))}
      <path d="M12 92 v-8 M20 94 v-6" stroke={I} strokeWidth="2" strokeLinecap="round" opacity=".7" />
    </>
  ),

  basket: () => (
    <>
      <path d="M14 46 h72 l-8 40 H 22 Z" fill={A} />
      <path d="M22 46 l4 40 M36 46 l2 40 M50 46 v40 M64 46 l-2 40 M78 46 l-4 40 M18 60 h64 M20 72 h60" stroke={P} strokeWidth="1.8" opacity=".5" />
      <path d="M30 46 C 30 26, 70 26, 70 46" fill="none" stroke={I} strokeWidth="4" strokeLinecap="round" />
      <path d="M14 46 h72" stroke={I} strokeWidth="4" strokeLinecap="round" />
      <circle cx="30" cy="36" r="8" fill={B} />
      <path d="M30 28 c 2 -4, 6 -4, 8 -2" fill="none" stroke={I} strokeWidth="2" strokeLinecap="round" />
      <ellipse cx="60" cy="36" rx="9" ry="7" fill={G} />
      <path d="M52 36 h16" stroke={P} strokeWidth="1.6" opacity=".6" />
    </>
  ),

  'moon-line': () => (
    <>
      <path d="M60 12 A 36 36 0 1 0 88 66 A 28 28 0 1 1 60 12 Z" fill="none" stroke={A} strokeWidth="2" />
      <Dot x={22} y={26} r={1.4} fill={A} />
      <Dot x={16} y={74} r={1.2} fill={A} />
    </>
  ),

  bow: () => (
    <>
      <path d="M50 50 C 40 30, 14 26, 10 44 C 8 56, 22 62, 34 58 C 42 56, 48 52, 50 50 Z" fill={A} />
      <path d="M50 50 C 60 30, 86 26, 90 44 C 92 56, 78 62, 66 58 C 58 56, 52 52, 50 50 Z" fill={A} />
      <path d="M50 50 C 44 62, 36 72, 30 84 C 38 82, 44 76, 50 68 C 56 76, 62 82, 70 84 C 64 72, 56 62, 50 50 Z" fill={A} opacity=".85" />
      <path d="M18 44 C 24 38, 32 38, 40 46 M82 44 C 76 38, 68 38, 60 46" fill="none" stroke={P} strokeWidth="1.6" opacity=".7" />
      <ellipse cx="50" cy="50" rx="7" ry="6" fill={B} />
      <Dot x={14} y={80} r={2.8} fill={G} />
      <Dot x={22} y={90} r={2.4} fill={G} />
      <Dot x={86} y={80} r={2.8} fill={G} />
      <Dot x={78} y={90} r={2.4} fill={G} />
    </>
  ),
}

/* -------------------------------------------------------------- the kits */

const kit = (
  icon: string,
  fall: string,
  chars: string[],
  foot: string,
  where: string,
): Motif => ({ Icon: ICONS[icon] || ICONS.none, fall, chars, foot, where })

export const MOTIFS: Record<string, Motif> = {
  none: kit('none', 'spark', ['✦', '·', '✧'], '✦ · ✦', '✦ '),
  dino: kit('dino', 'leaf', ['✦', '●', '▲'], '▲ ● ▲', '▲ '),
  car: kit('car', 'pennant', ['★', '✦', '▮'], '★ ✦ ★', '★ '),
  truck: kit('truck', 'dot', ['★', '●', '✦'], '★ ● ★', '★ '),
  rocket: kit('rocket', 'star', ['★', '✦', '·'], '★ ✦ ★', '★ '),
  unicorn: kit('unicorn', 'star', ['★', '✦', '♥'], '★ ♥ ★', '★ '),
  crown: kit('crown', 'diamond', ['♛', '✦', '·'], '♛ ✦ ♛', '♛ '),
  wand: kit('wand', 'star', ['✦', '✧', '·'], '✦ ✧ ✦', '✦ '),
  fish: kit('fish', 'dot', ['○', '◦', '·'], '○ ◦ ○', '○ '),
  ship: kit('ship', 'pennant', ['☠', '✦', '·'], '⚓ ✦ ⚓', '⚓ '),
  bolt: kit('bolt', 'bolt', ['★', '✦', '⚡'], '★ ⚡ ★', '⚡ '),
  giraffe: kit('giraffe', 'leaf', ['✿', '●', '·'], '✿ ● ✿', '✿ '),
  monstera: kit('monstera', 'leaf', ['❀', '✿', '·'], '❀ ✿ ❀', '❀ '),
  barn: kit('barn', 'dot', ['✿', '●', '·'], '✿ ● ✿', '✿ '),
  excavator: kit('excavator', 'square', ['▲', '■', '·'], '▲ ■ ▲', '▲ '),
  train: kit('train', 'dot', ['○', '●', '·'], '● ○ ●', '● '),
  'cloud-rainbow': kit('cloud-rainbow', 'star', ['★', '✦', '✧'], '★ ✦ ★', '★ '),
  teddy: kit('teddy', 'heart', ['♥', '♡', '·'], '♡ ♥ ♡', '♥ '),
  ballet: kit('ballet', 'petal', ['♡', '✿', '·'], '✿ ♡ ✿', '✿ '),
  controller: kit('controller', 'square', ['▮', '■', '▲'], '■ ▲ ■', '■ '),
  butterfly: kit('butterfly', 'petal', ['✿', '❀', '·'], '✿ ❀ ✿', '✿ '),
  circus: kit('circus', 'pennant', ['★', '✦', '●'], '★ ● ★', '★ '),
  candy: kit('candy', 'dot', ['●', '✦', '♥'], '● ✦ ●', '● '),
  icecream: kit('icecream', 'dot', ['●', '✦', '·'], '● ✦ ●', '● '),
  pizza: kit('pizza', 'dot', ['●', '✦', '·'], '● ✦ ●', '● '),
  football: kit('football', 'pennant', ['★', '●', '·'], '★ ● ★', '★ '),
  palette: kit('palette', 'drop', ['●', '✦', '✧'], '● ✦ ●', '● '),
  flask: kit('flask', 'dot', ['○', '⚛', '·'], '○ ⚛ ○', '○ '),
  'moon-stars': kit('moon-stars', 'star', ['☾', '★', '·'], '☾ ★ ☾', '☾ '),
  sparkle: kit('sparkle', 'spark', ['✦', '✧', '·'], '✦ ✧ ✦', '✦ '),
  disco: kit('disco', 'spark', ['✦', '◆', '·'], '✦ ◆ ✦', '✦ '),
  groovy: kit('groovy', 'petal', ['✿', '❀', '☮'], '✿ ☮ ✿', '✿ '),
  popper: kit('popper', 'confetti', ['✦', '★', '·'], '✦ ★ ✦', '✦ '),
  balloon: kit('balloon', 'balloon', ['♥', '★', '·'], '♥ ★ ♥', '♥ '),
  rings: kit('rings', 'petal', ['♡', '♥', '·'], '♡ ♥ ♡', '♥ '),
  eucalyptus: kit('eucalyptus', 'leaf', ['❀', '✿', '·'], '❀ ✿ ❀', '❀ '),
  rose: kit('rose', 'petal', ['❀', '♥', '·'], '❀ ♥ ❀', '❀ '),
  flourish: kit('flourish', 'spark', ['✦', '❦', '·'], '✦ ❦ ✦', '❦ '),
  wheat: kit('wheat', 'leaf', ['✶', '❀', '·'], '✶ ❀ ✶', '✶ '),
  arch: kit('arch', 'dot', ['✦', '●', '·'], '● ✦ ●', '● '),
  'deco-fan': kit('deco-fan', 'diamond', ['◆', '✦', '·'], '◆ ✦ ◆', '◆ '),
  grapes: kit('grapes', 'leaf', ['❀', '●', '·'], '❀ ● ❀', '❀ '),
  'wave-shell': kit('wave-shell', 'dot', ['○', '✦', '·'], '○ ✦ ○', '○ '),
  'line-leaf': kit('line-leaf', 'leaf', ['·', '✦', '·'], '·  ·  ·', '· '),
  lavender: kit('lavender', 'petal', ['❀', '✿', '·'], '❀ ✿ ❀', '❀ '),
  diamond: kit('diamond', 'diamond', ['◆', '✦', '✧'], '◆ ✦ ◆', '◆ '),
  heart: kit('heart', 'heart', ['♥', '♡', '·'], '♡ ♥ ♡', '♥ '),
  'cloud-moon': kit('cloud-moon', 'star', ['☾', '★', '·'], '☾ ★ ☾', '☾ '),
  blossom: kit('blossom', 'petal', ['❀', '✿', '·'], '❀ ✿ ❀', '❀ '),
  'balloons-duo': kit('balloons-duo', 'balloon', ['?', '♥', '★'], '♥ ? ♥', '♥ '),
  'mushroom-fox': kit('mushroom-fox', 'leaf', ['✿', '❀', '·'], '✿ ❀ ✿', '✿ '),
  lion: kit('lion', 'leaf', ['✦', '●', '·'], '✦ ● ✦', '✦ '),
  donut: kit('donut', 'dot', ['●', '♥', '·'], '● ♥ ●', '● '),
  star: kit('star', 'star', ['★', '✦', '·'], '★ ✦ ★', '★ '),
  dove: kit('dove', 'petal', ['✝', '✦', '·'], '✦ ✝ ✦', '✦ '),
  briefcase: kit('briefcase', 'confetti', ['✦', '■', '·'], '✦ ■ ✦', '✦ '),
  champagne: kit('champagne', 'dot', ['✦', '○', '·'], '✦ ○ ✦', '✦ '),
  lightbulb: kit('lightbulb', 'spark', ['✦', '★', '·'], '✦ ★ ✦', '✦ '),
  globe: kit('globe', 'dot', ['◆', '●', '·'], '◆ ● ◆', '◆ '),
  puzzle: kit('puzzle', 'square', ['■', '●', '▲'], '■ ● ▲', '■ '),
  trophy: kit('trophy', 'star', ['★', '✦', '·'], '★ ✦ ★', '★ '),
  sailboat: kit('sailboat', 'dot', ['⚓', '○', '·'], '⚓ ○ ⚓', '⚓ '),
  circuit: kit('circuit', 'square', ['◆', '■', '·'], '◆ ■ ◆', '◆ '),
  'ribbon-heart': kit('ribbon-heart', 'heart', ['♥', '✦', '·'], '♥ ✦ ♥', '♥ '),
  fireworks: kit('fireworks', 'spark', ['✦', '★', '✧'], '✦ ★ ✦', '✦ '),
  pine: kit('pine', 'snow', ['❄', '★', '·'], '❄ ★ ❄', '❄ '),
  snowflake: kit('snowflake', 'snow', ['❄', '❅', '·'], '❄ ❅ ❄', '❄ '),
  'pumpkin-ghost': kit('pumpkin-ghost', 'moon', ['☾', '★', '·'], '☾ ★ ☾', '☾ '),
  'egg-bunny': kit('egg-bunny', 'egg', ['✿', '❀', '·'], '✿ ❀ ✿', '✿ '),
  'sun-wave': kit('sun-wave', 'dot', ['☀', '○', '·'], '☀ ○ ☀', '☀ '),
  'autumn-leaf': kit('autumn-leaf', 'leaf', ['❀', '✦', '·'], '❀ ✦ ❀', '❀ '),
  tulip: kit('tulip', 'petal', ['❀', '✿', '♥'], '❀ ✿ ❀', '❀ '),
  churchkhela: kit('churchkhela', 'leaf', ['❀', '●', '·'], '❀ ● ❀', '❀ '),
  qvevri: kit('qvevri', 'leaf', ['❀', '●', '·'], '❀ ● ❀', '❀ '),
  wineglass: kit('wineglass', 'dot', ['✦', '●', '·'], '✦ ● ✦', '✦ '),
  croissant: kit('croissant', 'dot', ['☕', '✦', '·'], '✦ ☕ ✦', '✦ '),
  grill: kit('grill', 'spark', ['✦', '▲', '·'], '✦ ▲ ✦', '✦ '),
  mic: kit('mic', 'note', ['♪', '♫', '✦'], '♪ ♫ ♪', '♪ '),
  popcorn: kit('popcorn', 'star', ['★', '✦', '·'], '★ ✦ ★', '★ '),
  dice: kit('dice', 'square', ['●', '■', '▲'], '● ■ ▲', '● '),
  cards: kit('cards', 'diamond', ['♠', '♥', '♦', '♣'], '♠ ♥ ♦ ♣', '♦ '),
  mask: kit('mask', 'spark', ['✦', '◆', '·'], '✦ ◆ ✦', '✦ '),
  house: kit('house', 'heart', ['♥', '✦', '·'], '♥ ✦ ♥', '♥ '),
  teacup: kit('teacup', 'petal', ['❀', '✿', '·'], '❀ ✿ ❀', '❀ '),
  mountain: kit('mountain', 'leaf', ['▲', '✦', '·'], '▲ ✦ ▲', '▲ '),
  tent: kit('tent', 'star', ['★', '✦', '·'], '★ ✦ ★', '★ '),
  umbrella: kit('umbrella', 'dot', ['☀', '○', '·'], '☀ ○ ☀', '☀ '),
  'watering-can': kit('watering-can', 'petal', ['❀', '✿', '·'], '❀ ✿ ❀', '❀ '),
  basket: kit('basket', 'leaf', ['✿', '●', '·'], '✿ ● ✿', '✿ '),
  'moon-line': kit('moon-line', 'dot', ['·', '☾', '·'], '·  ☾  ·', '· '),
  bow: kit('bow', 'heart', ['♥', '✦', '·'], '♥ ✦ ♥', '♥ '),
}

export const getMotif = (id?: string | null): Motif => MOTIFS[id || 'none'] || MOTIFS.none
